import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import Modal from "@material-ui/core/Modal";
import { Button, Backdrop } from "@material-ui/core";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

// sass
import "../../scss/common.scss";
import "../../scss/component.scss";
import "../../scss/component/form.scss";
import "../../scss/component/icon.scss";

import { signalApiClient } from "../../utils/apiClient";
import {
  FReservationFlight,
  FReservationFlights,
  flightSearchResponse,
} from "../../types/FlightV2";
import { airportGroup, compositeAirport } from "../../config/Airport";
import { isOneWayAdditionalFlightEnabled } from "../../config/featureFlags";
import {
  getFlightCodeLabel,
  getFlightNumberForDisplay,
  getFlightTime,
  isVacancy,
} from "../../utils/flight";
import {
  createReserveFlightNumber,
  getConnectionFlights,
  getConnectionFlightPrices,
  getFlightClassLabel,
  isConnection,
  sortFlights,
} from "../../utils/flightV2";
import { PlanSelectFormData } from "../../types/planSelectForm";

/**
 * 区間追加フライト選択モーダル（issue #3015 改訂2 / #3285 で往復・片道の選び方を変更）。
 *
 * モード:
 * - "round-trip": 新規追加。1便選んだあと、確定エリアで「復路も選択する」か「この便のみで確定（片道）」を選ぶ。
 *                 復路まで選べば親に2件、片道で確定すれば1件返す
 * - "edit":       既存便の編集。通常は1件返して置換するだけだが、
 *                 片道の追加区間を編集している場合は確定エリアから復路を足せる（この時は2件返す）
 *
 * #3285 での変更点:
 * - 旧「追加タイプ」トグル（往復で追加 / 片道のみ追加）を廃止。先に往復か片道かを決める必要をなくした
 * - 片道で追加した区間に、あとから復路を足せるようにした（一度削除して作り直す必要がなくなった）
 *
 * 共通:
 * - 航空会社はメイン往復で選択済みのもの固定
 * - 中日搭乗日は select ドロップダウン（旅程の中の任意の日付）
 * - 中日区間（出発空港 → 到着空港）は全国の空港から選択可
 */
type SelectionEntry = {
  airline: string;
  from: string;
  to: string;
  date: string;            // YYYYMMDD
  flight: FReservationFlight[];
  direction: "inbound" | "outbound";
};

/**
 * 編集モードで「同じ round-trip の対方向の便」情報。
 * これがあれば、新便を選ぶ際に対方向との時系列整合（+2h ルール）を編集モードでも適用できる。
 *
 * 例: 復路を編集中 → paired は往路 → 新復路の出発が往路到着+2h 以降のみ通す
 *     往路を編集中 → paired は復路 → 新往路の到着が復路出発-2h 以前のみ通す
 */
type PairedFlight = {
  date: string;            // YYYYMMDD: 対方向便の搭乗日
  arrivalTime?: string;    // HHMM: 対方向便の最終到着時刻（編集対象が outbound のとき必須）
  departureTime?: string;  // HHMM: 対方向便の出発時刻（編集対象が inbound のとき必須）
};

/** 日付ごとの追加区間サマリ1件（issue #3285 追加改修）。親の additionalFlightLegs 由来 */
type AdditionalSegmentOnDate = {
  order: number;
  direction: "inbound" | "outbound";
  useDate: string;
  from: string;
  to: string;
  dep: string;   // HHMM
  arv: string;   // HHMM
};

type Props = {
  isShow: boolean;
  mode?: "round-trip" | "edit" | "add-return";
  airline: string;
  tripDates: string[];              // 旅程に含まれる全日付（YYYYMMDD 配列）
  /**
   * 中日搭乗日として選択できない日付（select で無効化）。
   *
   * issue #3285 以降は**メイン往復便の搭乗日のみ**が渡ってくる。
   * 追加区間が既にある日は同じ日に2つ目の区間を置けるようにするためブロックしない。
   */
  usedDates?: string[];
  /**
   * 日付（YYYYMMDD）ごとに既に設定されている追加区間（issue #3285 追加改修）。
   * 「その日に既に区間が入っている」ことを中日搭乗日の選択時に見せるために使う。
   */
  additionalSegmentsByDate?: Record<string, AdditionalSegmentOnDate[]>;
  /**
   * 上記の表示から除外する order（issue #3285 追加改修）。
   * 編集中 / 復路追加中の区間**自身**を「この日に設定済み」と出すと、
   * 別の区間が既にあるように読めてしまうため、操作対象の order は除外する。
   */
  excludeSegmentOrder?: number;
  /**
   * mode "add-return" 用（issue #3285 追加改修）: 既存の往路便から導出した復路の初期条件。
   * これが渡ると復路ステップから開始し、確定時は復路1件だけを返す。
   */
  addReturnInitial?: {
    date: string;               // 復路の既定日付（＝往路と同日）YYYYMMDD
    from: string;               // 往路の到着空港（＝復路の出発空港）
    to: string;                 // 往路の出発空港（＝復路の到着空港）
    inboundArrivalTime: string; // 往路の最終到着時刻 HHMM（同日 +2h ルールの基準）
  };
  // 新規追加（round-trip）用: 往路（inbound = 1ステップ目）の既定日付
  initialInboundDate?: string;
  // 編集（edit）用
  editDirection?: "inbound" | "outbound";
  editDate?: string;
  /**
   * 編集対象が片道区間（対方向の無い order>=2）のとき true。
   * 片道区間は滞在ハブ発とは限らない（例: 追加済み片道の帰り便）ため③の判定を行わない
   */
  editIsOneWay?: boolean;
  pairedFlight?: PairedFlight;      // M1: 編集モードで対方向の時系列制約を適用するための情報
  // 編集対象の現在の出発/到着空港。編集モードでセットされていれば select を初期選択状態にする
  editFrom?: string;
  editTo?: string;
  /**
   * 編集対象の現在の便を表す識別子（createReserveFlightNumber の戻り値）。
   * 編集モードで開いた直後の自動検索後、この便を選択状態に復元するために使う（issue #3285）。
   */
  editCurrentFlightNumber?: string;
  defaultFrom?: string;
  planSelectFormData: PlanSelectFormData;
  callback: () => void;
  /**
   * 確定時。
   * - round-trip: 復路まで選んだ場合は entries.length === 2（[往路, 復路]）、片道確定なら 1
   * - edit:       通常は entries.length === 1（置換）。
   *               片道区間に復路を足した場合は 2（[置換する往路, 追加する復路]）（issue #3285）
   */
  onConfirm: (entries: SelectionEntry[]) => void;
};

const AdditionalFlightModal: React.FC<Props> = ({
  isShow,
  mode = "round-trip",
  airline,
  tripDates,
  usedDates = [],
  additionalSegmentsByDate = {},
  excludeSegmentOrder,
  addReturnInitial,
  initialInboundDate,
  editDirection,
  editDate,
  editIsOneWay = false,
  pairedFlight,
  editFrom,
  editTo,
  editCurrentFlightNumber,
  defaultFrom,
  planSelectFormData,
  callback,
  onConfirm,
}) => {
  const { t, i18n } = useTranslation();

  /**
   * 現在のステップ:
   * - round-trip: "inbound" (往路選択中) → "outbound" (復路選択中)
   * - edit: 編集対象の direction
   */
  const initialStep: "inbound" | "outbound" =
    mode === "edit" ? (editDirection ?? "inbound")
      : mode === "add-return" ? "outbound"  // 復路だけを足すので最初から復路ステップ
        : "inbound";
  const [currentStep, setCurrentStep] = useState<"inbound" | "outbound">(initialStep);

  /**
   * 1ステップ目（往路）で確定した選択を保持。
   *
   * これが非 null ＝「往路を確定して復路ステップに進んだ状態」であり、
   * round-trip の新規追加・edit の復路後追い追加（issue #3285）で共通に使う。
   * 確定時に非 null なら [往路, 復路] の2件、null なら1件を親に返す。
   */
  const [firstStepSelection, setFirstStepSelection] = useState<SelectionEntry | null>(null);

  /**
   * 編集モードで開いた直後に1回だけ自動検索を走らせるためのフラグ（issue #3285）。
   * 日付・出発/到着空港は pre-fill 済みなので、開いた時点で検索して現在の便を選択状態にすると
   * 「復路だけ足したい」ケースで往路を選び直す手間が消える（変更フロー全体の使い勝手も改善）。
   */
  const [shouldAutoSearch, setShouldAutoSearch] = useState<boolean>(false);

  /**
   * 復路ステップでの選択制約（復路を選ぶ際に「対方向 = 往路」基準で絞り込む）:
   * - date        : 復路の日付は往路日付以降のみ選択可
   * - arrivalTime : 同日の場合、復路出発時刻は 往路到着時刻 + 2 時間 以降のみ表示
   *                 乗継便の場合は最終到着時刻を使用
   *
   * round-trip モード: 往路選択 → 復路ステップ遷移時にセット
   * edit モード（outbound 編集）: pairedFlight（=対方向の往路）からセット
   */
  const [outboundConstraint, setOutboundConstraint] = useState<
    { date: string; arrivalTime: string } | null
  >(null);

  /**
   * 往路（inbound）編集時の選択制約（M1: edit モードで往路を編集する際の +2h ルール逆方向）。
   * - date          : 往路の日付は復路日付以前のみ選択可
   * - departureTime : 同日の場合、往路の到着時刻は 復路出発時刻 - 2 時間 以前のみ表示
   *
   * edit モード（inbound 編集）で pairedFlight（=対方向の復路）からセット。
   * round-trip モードでは使用しない（inbound→outbound の順なので inbound 時には対方向情報が無い）
   */
  const [inboundConstraint, setInboundConstraint] = useState<
    { date: string; departureTime: string } | null
  >(null);

  // "0900" を 540 (分) に変換。不正値は null を返してフィルタ側でガード。
  // OKA は時々 "2400" を返すことがあるので、24:00 = 1440 分まで許容する（25:00 等は弾く）。
  const hhmmToMinutes = (hhmm: string): number | null => {
    if (!hhmm || hhmm.length < 4) return null;
    const h = parseInt(hhmm.substring(0, 2), 10);
    const m = parseInt(hhmm.substring(2, 4), 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (h < 0 || h > 24 || m < 0 || m > 59) return null;
    if (h === 24 && m > 0) return null; // 24:01+ は不正
    return h * 60 + m;
  };

  // 往路フライトの実際の到着時刻を取得（乗継便なら最終便の arv）。
  // arv が取得できない場合は null を返す（呼び出し側で制約をスキップして安全側に倒す）。
  const getActualArrivalTime = (f: FReservationFlight): string | null => {
    if (isConnection(f) && f.FReservationConnectionFlight) {
      const legs = getConnectionFlights(f.FReservationConnectionFlight, []);
      if (legs.length > 0) {
        const last = legs[legs.length - 1];
        if (last && last.arv) return last.arv;
      }
    }
    return f.arv ?? null;
  };

  /**
   * FE 段階リリースゲート（issue #3015 拡張）: 片道での区間追加を許可するフラグ。
   * 解放環境は src/config/featureFlags.ts でソース管理する（functions 側 featureFlags と対）。
   * ARNK は OTS 側システムが発券時に自動付与するため実装は不要。
   * FE だけ解放すると片道予約が /reserve のバリデーションで必ず 400 になるため、両者は同時に解放すること。
   *
   * issue #3285 以降はこのフラグが「確定エリアを2ボタンにするか」の判定も兼ねる。
   * OFF の場合は従来どおり 往路→復路 の2ステップ固定（片道で確定する導線が無い）。
   */
  const isOneWayAdditionalEnabled: boolean = isOneWayAdditionalFlightEnabled();

  /**
   * 中日区間の空港バリデーション（issue #3015 要望対応）。
   * 「同じ空港」= 空港コードが完全一致、または一方が他方の複合親コードである場合。
   *
   * - 兄弟空港（例: 伊丹 ITM と 関空 KIX / 神戸 UKB。いずれも大阪 OSA の子）は
   *   別空港のため同一とみなさない（往路発=伊丹 で着に神戸/関空を選んでも②は出ない）。
   * - 親子関係（例: 伊丹 ITM と 大阪 OSA、羽田 HND と 東京 TOKYO）は同一とみなす
   *   （複合コード「大阪（伊丹・関空・神戸）」を着に選び、往路発=伊丹 が含まれる場合は②を出す）。
   *
   * `compositeAirport`: 子コード → 親コード（例 ITM→OSA, KIX→OSA, HND→TOKYO）。
   */
  const isSameAirport = (a: string, b: string): boolean => {
    if (!a || !b) return false;
    return a === b || compositeAirport[a] === b || compositeAirport[b] === a;
  };

  /**
   * メイン往路便の出発空港（＝往路発空港。例: 羽田）。
   * ②着空港として選択不可 / ③出発空港選択時の警告 の基準に使う。
   * メイン往路便（order=1 or 未設定 / action="inbound"）の depapo を優先し、
   * 取得できない場合は商品設定 useAirport.departure にフォールバックする。
   */
  const outboundDepartureAirport: string = (() => {
    const mainInbound = planSelectFormData?.flight?.find(
      (pf) => (!pf.order || pf.order === 1) && pf.action === "inbound",
    );
    return (
      mainInbound?.entity?.[0]?.depapo ||
      planSelectFormData?.sellerProduct?.useAirport?.departure ||
      ""
    );
  })();

  // 検索条件
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [from, setFrom] = useState<string>(defaultFrom ?? "OKA");
  const [to, setTo] = useState<string>("");
  const [defaultFlights, setDefaultFlights] = useState<FReservationFlights[]>([]);
  const [flights, setFlights] = useState<FReservationFlights[]>([]);
  const [selectedFlights, setSelectedFlights] = useState<FReservationFlight | undefined>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [mixedMessage, setMixedMessage] = useState<string>("");

  // 直行/乗継ぎ用トグル
  const toggleRows = [
    { label: t("すべて"), value: "all" },
    { label: t("直行便のみ"), value: "direct" },
    { label: t("乗継便のみ"), value: "connecting" },
  ];
  const [flightToggleState, setFlightToggleState] = useState<string[]>(() => ["all"]);
  const [flightFilter, setFlightFilter] = useState<string>("all");
  const handleFlightToggle = (_e: React.MouseEvent<HTMLElement>, newValue: string[]) => {
    if (!newValue) return;
    setFlightToggleState(newValue);
    setFlightFilter(newValue.toString());
  };

  // 出発時間帯トグル
  const toggleTimeRows = [
    { label: t("すべて"), value: "all", fromTime: "", toTime: "" },
    { label: t("5時〜8時台"), value: "5_to_8", fromTime: "0500", toTime: "0859" },
    { label: t("9時〜12時台"), value: "9_to_12", fromTime: "0900", toTime: "1259" },
    { label: t("13時〜17時台"), value: "13_to_17", fromTime: "1300", toTime: "1759" },
    { label: t("18時〜22時台"), value: "18_to_22", fromTime: "1800", toTime: "2259" },
  ];
  const [flightTimeToggleState, setFlightTimeToggleState] = useState<string[]>(() => ["all"]);
  const handleFlightTimeToggle = (_e: React.MouseEvent<HTMLElement>, newValue: string[]) => {
    if (!newValue) return;
    setFlightTimeToggleState(newValue);
  };

  // モーダルを開いたとき / 親 props が変わったときに内部状態をリセット
  useEffect(() => {
    if (!isShow) return;
    if (mode === "edit") {
      setCurrentStep(editDirection ?? "inbound");
      setSelectedDate(editDate ?? "");
      // M1: 編集モードで対方向便の情報があれば、+2h ルールを edit でも適用
      if (pairedFlight) {
        if (editDirection === "outbound" && pairedFlight.arrivalTime) {
          // 復路を編集 → 対方向（往路）の到着 + 2h を出発下限に
          setOutboundConstraint({ date: pairedFlight.date, arrivalTime: pairedFlight.arrivalTime });
          setInboundConstraint(null);
        } else if (editDirection === "inbound" && pairedFlight.departureTime) {
          // 往路を編集 → 対方向（復路）の出発 - 2h を到着上限に
          setInboundConstraint({ date: pairedFlight.date, departureTime: pairedFlight.departureTime });
          setOutboundConstraint(null);
        } else {
          setOutboundConstraint(null);
          setInboundConstraint(null);
        }
      } else {
        setOutboundConstraint(null);
        setInboundConstraint(null);
      }
    } else if (mode === "add-return" && addReturnInitial) {
      // 片道区間への復路追加（issue #3285 追加改修）: 復路ステップから開始し、
      // 既存の往路便から日付・区間・+2h の制約を初期化する
      setCurrentStep("outbound");
      setSelectedDate(addReturnInitial.date);
      setOutboundConstraint({
        date: addReturnInitial.date,
        arrivalTime: addReturnInitial.inboundArrivalTime,
      });
      setInboundConstraint(null);
    } else {
      setCurrentStep("inbound");
      setSelectedDate(initialInboundDate ?? "");
      setOutboundConstraint(null);
      setInboundConstraint(null);
    }
    setFirstStepSelection(null);
    // editFrom/editTo（編集）や addReturnInitial（復路追加）が渡されていれば区間を初期選択しておく
    // （ユーザーが何度も空港を選び直さなくて済むよう pre-fill する）
    if (mode === "edit" && editFrom && editTo) {
      setFrom(editFrom);
      setTo(editTo);
    } else if (mode === "add-return" && addReturnInitial) {
      setFrom(addReturnInitial.from);
      setTo(addReturnInitial.to);
    } else {
      setFrom(defaultFrom ?? "OKA");
      setTo("");
    }
    setDefaultFlights([]);
    setFlights([]);
    setSelectedFlights(undefined);
    setFlightToggleState(["all"]);
    setFlightFilter("all");
    setFlightTimeToggleState(["all"]);
    setMixedMessage("");
    // 検索条件が揃っているモード（編集 / 復路追加）は開いた直後に自動検索する（issue #3285）。
    // 復路追加には「復元すべき現在の便」が無いので検索だけ走らせ、選択はユーザーに任せる。
    setShouldAutoSearch(
      (mode === "edit" && !!editFrom && !!editTo && !!editDate) ||
      (mode === "add-return" && !!addReturnInitial),
    );
  }, [isShow, mode, editDirection, editDate, initialInboundDate, defaultFrom, pairedFlight, editFrom, editTo, addReturnInitial]);

  /**
   * フライト検索。
   * @param autoSelectFlightNumber 指定すると、検索結果の中から createReserveFlightNumber が
   *   一致する便を選択状態にする（編集モードの自動検索で現在の便を復元するために使う / issue #3285）
   */
  const [searchAbortController, setSearchAbortController] = useState<AbortController | null>(null);
  const handleSearch = async (autoSelectFlightNumber?: string) => {
    if (!from || !to || !selectedDate) return;
    // ハードブロック（②着空港=往路発空港 / 発着地が同一）の区間は検索させない（要望対応 issue #3015）
    if (isSameAirport(to, outboundDepartureAirport) || isSameAirport(from, to)) return;
    // 進行中の検索をキャンセル
    if (searchAbortController) searchAbortController.abort();
    const ac = new AbortController();
    setSearchAbortController(ac);
    setLoading(true);
    setSelectedFlights(undefined);
    try {
      const res: AxiosResponse<flightSearchResponse> = await signalApiClient(ac).post(
        "/air/flight-search-additional",
        JSON.stringify({
          date: selectedDate,
          from: from,
          to: to,
          air_code: airline,
          via: 2,
          bookingClass: "Y",
        }),
      );
      const list = res.data?.flight?.FReservationFlights ?? [];
      const sorted = sortFlights(list) ?? [];
      setDefaultFlights(sorted);
      setFlights(sorted);
      // 編集モードの自動検索: 現在の便を選択状態に復元する（issue #3285）。
      // 満席（空席なしでラジオが disabled）の便は選択させない（確定ボタンが押せてしまうのを防ぐ）
      if (autoSelectFlightNumber) {
        for (const fs of sorted) {
          const hit = fs.FReservationFlight.find(
            (rf) =>
              createReserveFlightNumber(rf) === autoSelectFlightNumber &&
              isVacancy(airline, rf, planSelectFormData.searchParams.roomDetail),
          );
          if (hit) {
            setSelectedFlights({ ...hit });
            break;
          }
        }
      }
      setMixedMessage(sorted.length === 0 ? t("該当するフライトが見つかりませんでした。") ?? "" : "");
    } catch {
      setMixedMessage(t("フライト検索に失敗しました。") ?? "");
    } finally {
      setLoading(false);
    }
  };
  // モーダルを閉じる時 / unmount 時に進行中の検索を中断（m1: SectionSchedule 側の条件付きレンダリングで
  // isShow=false の中間状態を経ずに unmount される経路にも対応するため、cleanup 関数でも abort する）
  useEffect(() => {
    if (!isShow && searchAbortController) {
      searchAbortController.abort();
      setSearchAbortController(null);
    }
    return () => {
      // unmount 時の保険: ref ではなく state 参照だが、cleanup 時点の closure に
      // 最新コントローラーが入っているのでこれで十分
      if (searchAbortController) {
        searchAbortController.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, searchAbortController]);

  // 検索条件（日付・出発空港・到着空港）が変わったら、進行中の検索を中断 + 既存結果と選択をクリアして再検索を促す
  // 中断しないと、古い検索条件で取得した結果が後から到着して新条件と矛盾する画面状態になる
  useEffect(() => {
    if (searchAbortController) {
      searchAbortController.abort();
      setSearchAbortController(null);
    }
    setDefaultFlights([]);
    setFlights([]);
    setSelectedFlights(undefined);
    setMixedMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, from, to]);

  /**
   * 編集モードで開いた直後の自動検索（issue #3285）。
   *
   * 上の「検索条件が変わったらクリア」effect より後に宣言することが重要。
   * 同一コミットでは宣言順に effect が走るため、クリア → 自動検索 の順になり、
   * 自動検索の結果が直後にクリアされる事故を防げる。
   */
  useEffect(() => {
    if (!shouldAutoSearch) return;
    if (!isShow || (mode !== "edit" && mode !== "add-return")) {
      setShouldAutoSearch(false);
      return;
    }
    // 検索条件が揃うまで待つ（reset effect の setState 反映は次コミット）
    if (!selectedDate || !from || !to) return;
    // ハードブロック中は手動検索と同じく検索させない
    if (isSameAirport(to, outboundDepartureAirport) || isSameAirport(from, to)) {
      setShouldAutoSearch(false);
      return;
    }
    setShouldAutoSearch(false);
    void handleSearch(editCurrentFlightNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoSearch, isShow, mode, selectedDate, from, to]);

  // フィルター（直行/乗継 + 出発時間帯 + 復路 2時間ルール）を適用
  useEffect(() => {
    let filtered = [...defaultFlights];
    if (flightFilter === "direct") {
      filtered = filtered.filter((fs) => !isConnection(fs.FReservationFlight[0]));
    } else if (flightFilter === "connecting") {
      filtered = filtered.filter((fs) => isConnection(fs.FReservationFlight[0]));
    }
    const timeRow = toggleTimeRows.find((r) => r.value === flightTimeToggleState.toString());
    if (timeRow && timeRow.fromTime && timeRow.toTime) {
      filtered = filtered.filter((fs) => {
        const dep = fs.FReservationFlight[0].dep;
        return dep >= timeRow.fromTime && dep <= timeRow.toTime;
      });
    }
    // 復路ステップ: 同日の場合 往路到着時刻 + 2 時間 以降のみ
    // 到着時刻 / 出発時刻が不正な便は除外しないと安全側に倒す（null 返却で skip）
    // minDepMinutes が 1440 (24:00) を超える場合は同日で復路便を取れないため
    // フィルタを適用しない（空リスト誤表示防止 M2）→ ユーザーに「翌日以降を選択してください」と促す
    if (outboundConstraint && selectedDate === outboundConstraint.date) {
      const arrivalMin = hhmmToMinutes(outboundConstraint.arrivalTime);
      if (arrivalMin !== null) {
        const minDepMinutes = arrivalMin + 120;
        if (minDepMinutes < 1440) {
          filtered = filtered.filter((fs) => {
            const depMin = hhmmToMinutes(fs.FReservationFlight[0].dep);
            return depMin !== null && depMin >= minDepMinutes;
          });
        } else {
          // 同日復路が不可能 → 空リスト + ガイドメッセージで翌日以降選択を促す
          filtered = [];
          setMixedMessage(t("往路到着が遅いため同日の復路便はありません。翌日以降を選択してください。") ?? "");
        }
      }
    }
    // 往路編集モード (M1): 同日の場合 復路出発時刻 - 2 時間 以前のみ
    // 編集対象の inbound の到着時刻が対方向（復路）の出発時刻 - 2h 以前であることを要求
    if (inboundConstraint && selectedDate === inboundConstraint.date) {
      const depMin = hhmmToMinutes(inboundConstraint.departureTime);
      if (depMin !== null) {
        const maxArvMinutes = depMin - 120;
        if (maxArvMinutes >= 0) {
          filtered = filtered.filter((fs) => {
            const arv = getActualArrivalTime(fs.FReservationFlight[0]);
            if (arv === null) return true; // arv 不明便はフィルタしない（安全側）
            const arvMin = hhmmToMinutes(arv);
            return arvMin !== null && arvMin <= maxArvMinutes;
          });
        } else {
          // 同日往路が不可能（復路出発が早朝すぎ）→ 翌日以前選択を促す
          filtered = [];
          setMixedMessage(t("復路出発が早いため同日の往路便はありません。前日以前を選択してください。") ?? "");
        }
      }
    }
    setFlights(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightFilter, flightTimeToggleState, defaultFlights, outboundConstraint, inboundConstraint, selectedDate]);

  const isSelected = (f: FReservationFlight): boolean => {
    if (!selectedFlights) return false;
    return createReserveFlightNumber(f) === createReserveFlightNumber(selectedFlights);
  };

  const getAdultFee = (f: FReservationFlight): number => {
    const toPositive = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    let fee = toPositive(f?.adult_fee);
    if (f?.FReservationConnectionFlight && f.FReservationConnectionFlight[0]) {
      const cp = getConnectionFlightPrices(f.FReservationConnectionFlight[0], { adultTotalFee: 0, childTotalFee: 0 });
      fee += toPositive(cp.adultTotalFee);
    }
    if (fee > 0) return fee;
    return toPositive(f?.min_fee);
  };

  const handleFlightSelect = (f: FReservationFlight) => {
    setSelectedFlights({ ...f });
  };

  /**
   * 指定日に「他の」追加区間が設定されているかを返す（issue #3285 追加改修）。
   * 編集中 / 復路追加中の区間自身（excludeSegmentOrder）は除外する。
   * これを除外しないと、いま操作している区間が「この日に設定済みの別区間」として表示されてしまう。
   */
  const getOtherSegmentsOnDate = (ymd: string): AdditionalSegmentOnDate[] =>
    (additionalSegmentsByDate[ymd] ?? []).filter((s) => s.order !== excludeSegmentOrder);

  // 現在の検索条件・選択便から確定用エントリを組み立てる
  const buildCurrentSelection = (): SelectionEntry | null => {
    if (!selectedFlights) return null;
    return {
      airline,
      from,
      to,
      date: selectedDate,
      flight: [selectedFlights],
      direction: currentStep,
    };
  };

  /**
   * 「復路を選択する」押下時（issue #3285）。
   * 現在の選択を往路として保存し、復路ステップへ進む。
   *
   * round-trip の新規追加でも、片道区間の編集（＝復路の後追い追加）でも同じ処理。
   * edit モードでも firstStepSelection が入るため、確定時に2件返る（親側で往路置換 + 復路 append）。
   */
  const handleProceedToReturn = () => {
    const currentSelection = buildCurrentSelection();
    if (!currentSelection || !selectedFlights) return;

    setFirstStepSelection(currentSelection);
    setCurrentStep("outbound");
    // 復路の制約: 往路日付以降 / 同日なら 到着時刻 + 2 時間以降のみ
    // 乗継便の場合は最終到着時刻を使用。
    // arv が取れない便は出発時刻（dep）にフォールバックして制約を完全には外さない（M1: 安全側を維持）
    const arrivalTime = getActualArrivalTime(selectedFlights) ?? selectedFlights.dep ?? "";
    setOutboundConstraint({
      date: selectedDate,
      arrivalTime,
    });
    // 往路編集の制約（復路出発 -2h）は復路選択には無関係なので解除する。
    // 片道区間の編集では pairedFlight が無いので通常 null だが、防御的にクリアしておく。
    setInboundConstraint(null);
    // 復路用に初期化（出発・到着空港を swap、便選択をリセット、時間帯フィルタもリセット）
    // 中日搭乗日は往路と同日をデフォルトにして残す。空にすると出発/到着を設定しても
    // 検索ボタンが有効化されず操作に詰まるため（往路以降のみ選択可なので同日は常に有効）。
    setSelectedDate(selectedDate);
    setFrom(to);
    setTo(from);
    setDefaultFlights([]);
    setFlights([]);
    setSelectedFlights(undefined);
    setFlightToggleState(["all"]);
    setFlightFilter("all");
    setFlightTimeToggleState(["all"]);
    setMixedMessage("");
    // 復路は自動検索しない（往路とは条件が変わるためユーザーに検索させる）
    setShouldAutoSearch(false);
  };

  /**
   * 確定ボタン押下時（issue #3285 で整理）。
   *
   * firstStepSelection が入っていれば「往路を確定済み」なので [往路, 復路] の2件、
   * 入っていなければ片道1件（新規の片道追加 / 編集の置換）を親に返す。
   */
  const handleDecide = () => {
    const currentSelection = buildCurrentSelection();
    if (!currentSelection) return;
    onConfirm(firstStepSelection ? [firstStepSelection, currentSelection] : [currentSelection]);
    callback();
  };

  /**
   * 確定エリアを「復路を選択する / この便のみで確定（片道）」の2ボタンにするか（issue #3285）。
   *
   * - 新規追加の1便目（往路ステップ）
   * - 片道の追加区間を編集中（＝復路をここから後追いできる）
   *
   * いずれも片道リリースゲートが解放されている環境のみ。OFF の場合は従来どおり
   * 往路→復路 の2ステップ固定で、片道で確定する導線は出さない。
   * 復路ステップへ進んだ後（firstStepSelection あり）は選択済みなので単一ボタンに戻す。
   */
  const showTripTypeChoice: boolean =
    isOneWayAdditionalEnabled &&
    !firstStepSelection &&
    currentStep === "inbound" &&
    (mode === "round-trip" ||
      // 片道区間の編集のみ。往復区間の片方向編集で復路を足すと同一 order に2便目ができてしまう
      (mode === "edit" && editIsOneWay === true && editDirection === "inbound"));

  // 単一ボタン時に「確定」ではなく「往路を選択（＝復路ステップへ）」として振る舞うか。
  // 片道ゲート OFF の新規追加（従来どおりの2ステップ固定）だけが該当する。
  const singleButtonProceeds: boolean =
    !showTripTypeChoice && mode === "round-trip" && currentStep === "inbound";

  // ボタンテキスト
  const buttonLabel: string = (() => {
    if (singleButtonProceeds) return t("往路を選択") ?? "往路を選択";
    if (mode === "add-return") return t("復路を追加") ?? "復路を追加";
    if (currentStep === "outbound" && firstStepSelection) return t("復路を選択") ?? "復路を選択";
    if (mode === "edit") return t("変更を確定") ?? "変更を確定";
    return t("この便で確定") ?? "この便で確定";
  })();

  // 進捗ラベル（見出しの「（往路）」部分）。編集中に復路を足す場合も currentStep に追従させる
  const stepLabel: string = currentStep === "outbound" ? t("復路") ?? "復路" : t("往路") ?? "往路";

  /**
   * 見出し横の進捗表示（issue #3285）。
   * 片道ゲート ON では1便目の時点で往復/片道が未確定なので「ステップ 1 / 2」とは出さない。
   */
  const stepProgressLabel: string = (() => {
    // 片道区間への復路追加（issue #3285 追加改修）: 往路は既存のものを使うのでステップは1つだけ。
    // 見出しもボタンも「復路を追加」なので、ここは「なぜ1ステップなのか」を伝える文言にする
    if (mode === "add-return") return t("往路は追加済みの便を使用します") ?? "往路は追加済みの便を使用します";
    if (currentStep === "outbound" && firstStepSelection) {
      return mode === "edit"
        ? t("復路を追加") ?? "復路を追加"
        : t("ステップ 2 / 2") ?? "ステップ 2 / 2";
    }
    if (mode !== "round-trip") return "";
    return isOneWayAdditionalEnabled
      ? t("まず1便目を選択してください") ?? "まず1便目を選択してください"
      : t("ステップ 1 / 2") ?? "ステップ 1 / 2";
  })();

  // 中日区間の空港バリデーション（issue #3015 要望対応）
  // ② 着空港＝往路発空港（例: 那覇→羽田）: ハードブロック
  const arrivalIsOutboundDeparture: boolean = isSameAirport(to, outboundDepartureAirport);
  // 発着地が同一（例: 沖縄→沖縄）: ハードブロック
  const sameFromTo: boolean = isSameAirport(from, to);
  // 滞在ハブ空港（＝メイン便の到着空港。商品設定 useAirport.arrival。例: 那覇 OKA）
  const stayHubAirport: string = planSelectFormData?.sellerProduct?.useAirport?.arrival || "OKA";

  // ハードブロック（赤アラート + 検索/確定不可）。② を優先し、次に発着地同一。
  const hardBlock: boolean = arrivalIsOutboundDeparture || sameFromTo;
  const hardBlockMessage: string = arrivalIsOutboundDeparture
    ? t("往路発空港と同じ空港は追加航空行程の着空港として選択できません。区間を変更してください。") ?? ""
    : sameFromTo
      ? t("出発空港と到着空港が同じ空港です。区間を変更してください。") ?? ""
      : "";
  // ③ ソフト警告（続行可）。
  // 中日「往路」の出発空港が滞在ハブ（＝メイン到着空港・那覇）で「ない」ときに出す。
  // 中日往路は滞在ハブ（那覇）発が前提のため、それ以外（島/本土 発）は発着の確認を促す。
  // 復路は自動 swap で「島→那覇」となるため往路ステップのみに適用し、
  // 片道区間の再編集（editIsOneWay）はハブ発とは限らない正当ケースなので判定しない。
  //
  // issue #3285: 追加タイプのトグルを廃止したため、1便目の時点では往復/片道が未確定になった。
  // 旧条件の `tripType === "round"` は判定できないので外し、代わりに補足文
  // （片道で追加するなら問題ない旨）を添えて誤警告と受け取られないようにしている。
  const softWarnMessage: string = (() => {
    if (hardBlock) return "";
    if (
      currentStep === "inbound" &&
      !editIsOneWay &&
      !!from &&
      !isSameAirport(from, stayHubAirport)
    ) {
      return t("出発空港、到着空港をご確認ください。") ?? "";
    }
    return "";
  })();
  // ③の補足。往復/片道が未確定の状態で出る警告なので「片道ならこのままで良い」ことを明示する
  const softWarnNote: string =
    softWarnMessage && showTripTypeChoice
      ? t("片道のみで追加する場合は、このままで問題ありません。") ?? ""
      : "";

  // 検索ボタンの非活性判定（ハードブロック中は非活性とわかる見た目にする。③警告のみでは非活性にしない）
  const searchDisabled: boolean =
    !selectedDate || !from || !to || isLoading || hardBlock;

  const reset = () => {
    callback();
  };

  return (
    <div>
      <Modal
        open={isShow}
        onClose={reset}
        className="modal"
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <div className="modal-fixed">
          <div className="modal-fixed-heading">
            <p className="modal-fixed-heading-text">
              {mode === "edit"
                ? t("追加区間の変更")
                : mode === "add-return"
                  ? t("追加区間に復路を追加")
                  : t("航空行程を追加")}（{stepLabel}）
              {stepProgressLabel && (
                <span style={{ marginLeft: 12, fontSize: 12, color: "#888" }}>
                  {stepProgressLabel}
                </span>
              )}
            </p>
            <p className="modal-fixed-close">
              <Button onClick={callback} className="modal-fixed-close-button">
                <i className="modal-fixed-close-button-icon"></i>{t("閉じる")}
              </Button>
            </p>
          </div>

          <div className="flight-modal-fixed-content">
            <div className="wrap-content-900 wrap-padding-md">
              <p className="heading-2 fz-24-md mt-24 mb-24 mt-40-md">
                {airline}{t("から選択")}（{stepLabel}）
              </p>

              {/* 復路ステップ: 制約を明示（往路日付以降 / 同日なら +2 時間以降） */}
              {outboundConstraint && (
                <div className="box-light-gray small mb-16" style={{ padding: "10px 14px", fontSize: 13 }}>
                  <p className="c-navy">
                    <i className="icon-info mr-5" aria-hidden></i>
                    {t("復路は往路日付以降のみ、同日の場合は往路到着時刻の2時間後以降のフライトのみ選択できます。")}
                  </p>
                </div>
              )}

              {/* 往復/片道の選び方の案内（issue #3285）。
                  追加タイプのトグルは廃止し、便を選んだあと確定エリアで決める方式にした */}
              {showTripTypeChoice && (
                <div className="box-light-gray small mb-16" style={{ padding: "10px 14px", fontSize: 13 }}>
                  <p className="c-navy">
                    <i className="icon-info mr-5" aria-hidden></i>
                    {mode === "edit"
                      ? t("便を選んだあと、下部で「復路を選択する」か「この便のみで確定（片道）」を選べます。")
                      : t("便を選んだあと、下部で「復路を選択する」か「この便のみで確定（片道）」を選べます。往復にするか片道にするかを先に決める必要はありません。")}
                  </p>
                </div>
              )}

              <dl>
                {/* 中日搭乗日（select dropdown） */}
                <div className="d-f-md ai-c">
                  <dt className="c-navy fz-13 fz-16-md fw-b mb-8 mb-0-md fx-sh w-110-md">
                    {t("中日搭乗日")}
                  </dt>
                  <dd className="fx-1">
                    <div className="form-select_list-wrap">
                      <div className="select_box-default">
                        <select
                          className="form-select_list"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                        >
                          <option value="">{t("選択してください")}</option>
                          {tripDates.map((d) => {
                            // メイン往復便の搭乗日は選択不可（issue #3285: 追加区間がある日は選べる）
                            const isUsed = usedDates.includes(d) && d !== selectedDate;
                            // 復路ステップ / outbound 編集モード: 往路日付より前は不可
                            const isBeforeOutbound =
                              !!outboundConstraint && d < outboundConstraint.date;
                            // 往路 編集モード (M1): 復路日付より後は不可
                            const isAfterInbound =
                              !!inboundConstraint && d > inboundConstraint.date;
                            const disabled = isUsed || isBeforeOutbound || isAfterInbound;
                            const suffix = isBeforeOutbound
                              ? `（${t("往路より前") ?? "往路より前"}）`
                              : isAfterInbound
                                ? `（${t("復路より後") ?? "復路より後"}）`
                                : isUsed
                                  ? `（${t("メイン往復") ?? "メイン往復"}）`
                                  : "";
                            /* ▼▼▼ A案: その日に追加区間が設定済みであることを option に注記する ▼▼▼
                               （B案と二択。不要な方をこの行と下の {addedNote} ごと削除してください）
                               ※ <option> はテキストしか持てないため、モーダル側の A案 はこの形になる */
                            const daySegments = getOtherSegmentsOnDate(d);
                            const addedNote = daySegments.length > 0
                              ? `（${t("追加区間")}${Array.from(new Set(daySegments.map((s) => s.order - 1))).join("・")} ${t("設定済み")}）`
                              : "";
                            /* ▲▲▲ A案ここまで ▲▲▲ */
                            return (
                              <option key={d} value={d} disabled={disabled}>
                                {dayjs(d, "YYYYMMDD").locale(i18n.language).format(t("YYYY年MM月DD日（ddd）") ?? "YYYY年MM月DD日（ddd）")}
                                {suffix}
                                {addedNote}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {/* ▼▼▼ B案: 選択中の日付に既に入っている追加区間のフライトスケジュールを表示 ▼▼▼
                        （A案と二択。不要な方をこのブロックごと削除してください）
                        ※ <option> 内にはリッチな表示ができないため、select の「下」に出す形になる */}
                    {getOtherSegmentsOnDate(selectedDate).length > 0 && (
                      <div className="box-light-gray small mt-8" style={{ padding: "8px 12px", fontSize: 12 }}>
                        <p className="c-blue_gray fw-b mb-2">{t("この日に設定済みの追加区間")}</p>
                        {getOtherSegmentsOnDate(selectedDate).map((s, sIdx) => (
                          <p key={"sel-day-seg-" + sIdx} className="c-navy">
                            {t("追加区間")}{s.order - 1}
                            {"　"}
                            {getFlightTime(s.dep)} {s.from} → {s.to} {getFlightTime(s.arv)}
                            {"　"}
                            <span className="c-blue_gray">
                              （{s.direction === "inbound" ? t("往路") : t("復路")}）
                            </span>
                          </p>
                        ))}
                      </div>
                    )}
                    {/* ▲▲▲ B案ここまで ▲▲▲ */}
                  </dd>
                </div>

                {/* 中日区間 */}
                <div className="d-f-md ai-c mt-16 mt-24-md">
                  <dt className="c-navy fz-13 fz-16-md fw-b mb-8 mb-0-md fx-sh w-110-md">
                    {t("中日区間")}
                  </dt>
                  <dd className="fx-1">
                    <div className="d-f ai-e">
                      <div className="fx-1">
                        <p className="c-blue_gray fz-12 fz-14-md mb-5">{t("出発空港")}</p>
                        <div className="form-select_list-wrap">
                          <div className="select_box-default">
                            <select
                              className="form-select_list"
                              value={from}
                              onChange={(e) => {
                                setFrom(e.target.value);
                                // 到着空港をリセットして選び直しを促す
                                setTo("");
                              }}
                            >
                              <option value="">{t("選択してください")}</option>
                              {airportGroup.map((group, gIdx) => (
                                <optgroup label={group.groupName} key={"from-grp-" + gIdx}>
                                  {Object.keys(group.airportList).map((code, i) => (
                                    <option
                                      className="form-select_list-options"
                                      value={code}
                                      key={"from-" + gIdx + "-" + i}
                                    >
                                      {t(`Airport.${group.airportList[code as keyof typeof group.airportList]}`)}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <i className="icon-right-arrow fx-sh fz-12 ml-16 mr-16 mb-16"></i>
                      <div className="fx-1">
                        <p className="c-blue_gray fz-12 fz-14-md mb-5">{t("到着空港")}</p>
                        <div className="form-select_list-wrap">
                          <div className="select_box-default">
                            <select
                              className="form-select_list"
                              value={to}
                              onChange={(e) => setTo(e.target.value)}
                            >
                              <option value="">{t("選択してください")}</option>
                              {airportGroup.map((group, gIdx) => (
                                <optgroup label={group.groupName} key={"to-grp-" + gIdx}>
                                  {Object.keys(group.airportList)
                                    // ① 出発空港と同じ空港は到着空港として表示しない（往路・復路どちらも）
                                    .filter((code) => !from || code !== from)
                                    .map((code, i) => (
                                    <option
                                      className="form-select_list-options"
                                      value={code}
                                      key={"to-" + gIdx + "-" + i}
                                    >
                                      {t(`Airport.${group.airportList[code as keyof typeof group.airportList]}`)}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </dd>
                </div>

                {/* 直行/乗継ぎ */}
                <div className="d-f-md ai-c mt-16 mt-24-md">
                  <dt className="c-navy fz-13 fz-16-md fw-b mb-8 mb-0-md fx-sh w-110-md">
                    {t("直行/乗継ぎ")}
                  </dt>
                  <dd className="fx-1">
                    <ToggleButtonGroup
                      value={flightToggleState}
                      exclusive
                      onChange={handleFlightToggle}
                      aria-label="all"
                      className="box-toggle_button"
                    >
                      {toggleRows.map((row, index) => (
                        <ToggleButton
                          key={"toggle_f_add" + index}
                          value={row.value}
                          aria-label={row.value}
                          className="box-toggle_button-item"
                        >
                          {row.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </dd>
                </div>

                {/* 出発時間帯 */}
                <div className="d-f-md ai-c mt-16 mt-24-md">
                  <dt className="c-navy fz-13 fz-16-md fw-b mb-8 mb-0-md fx-sh w-110-md">
                    {t("出発時間帯")}
                  </dt>
                  <dd className="fx-1">
                    <ToggleButtonGroup
                      value={flightTimeToggleState}
                      exclusive
                      onChange={handleFlightTimeToggle}
                      aria-label="all"
                      className="box-toggle_button"
                    >
                      {toggleTimeRows.map((row, index) => (
                        <ToggleButton
                          key={"toggle_t_add" + index}
                          value={row.value}
                          aria-label={row.value}
                          className="box-toggle_button-item"
                        >
                          {row.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </dd>
                </div>
              </dl>

              {/* ②/発着地同一: ハードブロックのアラート（赤・検索/確定不可）（要望対応 issue #3015） */}
              {hardBlock && (
                <div className="box-light-gray small mt-16" style={{ padding: "10px 14px", fontSize: 13 }}>
                  <p className="c-red fw-b">
                    <i className="icon-info mr-5" aria-hidden></i>
                    {hardBlockMessage}
                  </p>
                </div>
              )}

              {/* ③・オープンジョー成立性: 予測アラート（警告のみ・続行可）（要望対応 issue #3015） */}
              {softWarnMessage && (
                <div className="box-light-gray small mt-16" style={{ padding: "10px 14px", fontSize: 13 }}>
                  <p className="fw-b" style={{ color: "#F85400" }}>
                    <i className="icon-info mr-5" aria-hidden></i>
                    {softWarnMessage}
                  </p>
                  {softWarnNote && (
                    <p className="fz-12 c-blue_gray mt-5">{softWarnNote}</p>
                  )}
                </div>
              )}

              {/* 検索ボタン（手動検索）。日付・出発空港・到着空港が揃ったら活性化 */}
              <div className="ta-c mt-24 mt-32-md mb-16">
                <button
                  type="button"
                  onClick={() => handleSearch()}
                  disabled={searchDisabled}
                  className="button-medium-orange padding-large w-100per"
                  style={{
                    maxWidth: 320,
                    // 非活性時はグレーアウトして「押せない」ことを明示（.button-medium-disabled と同じ配色）
                    ...(searchDisabled
                      ? {
                          backgroundColor: "#DCE0EA",
                          color: "#7F95B7",
                          boxShadow: "none",
                          cursor: "not-allowed",
                        }
                      : {}),
                  }}
                >
                  {t("フライトを検索する")}
                </button>
              </div>

              {/* フライト選択 */}
              <LoadingOverlay active={isLoading} spinner className="loading-small-height">
                <form action="" onSubmit={(e) => e.preventDefault()}>
                  <div className="box_select-flight-schedule label-mulch">
                    <ul className="list-note mt-24 mb-50">
                      <li>{t("料金は1人あたりの大人料金を表示しています。")}</li>
                    </ul>

                    <div className="box_select-flight-schedule-title">
                      <p className="box_select-flight-schedule-date">
                        {selectedDate && dayjs(selectedDate, "YYYYMMDD").locale(i18n.language).format(t("YYYY年MM月DD日（ddd）") ?? "YYYY年MM月DD日（ddd）")}
                      </p>
                      <div className="box_select-flight-schedule-difference">
                        {t("料金")}
                        <span className="fz-10 fz-12-md">（{t("１人あたり")}）</span>
                      </div>
                    </div>

                    <ul className="box_select-flight-schedule-list">
                      {!selectedDate || !to ? (
                        <li className="p-16">{t("中日搭乗日と中日区間を選択してください")}</li>
                      ) : defaultFlights.length === 0 && !mixedMessage ? (
                        <li className="p-16">{t("「フライトを検索する」ボタンを押してください")}</li>
                      ) : flights && flights.length > 0 ? (
                        flights.map((fs, fIndex) => (
                          <React.Fragment key={"add-flight-" + fIndex}>
                            <li
                              className={fs.FReservationFlight.length > 1
                                ? "box_select-flight-schedule-list-item mulch"
                                : "box_select-flight-schedule-list-item"}
                            >
                              <div className="box_select-flight-schedule-list-item-time_table">
                                {isConnection(fs.FReservationFlight[0]) ? (
                                  <div className="box_select-flight-schedule-list-item-time_table-label orange">
                                    <span className="box_select-flight-schedule-list-item-time_table-label-text">{t("乗継便")}</span>
                                  </div>
                                ) : (
                                  <div className="box_select-flight-schedule-list-item-time_table-label green">
                                    <span className="box_select-flight-schedule-list-item-time_table-label-text">{t("直行便")}</span>
                                  </div>
                                )}

                                <div className="box_select-flight-schedule-list-item-time_table-time_schedule">
                                  <ul className="box_select-flight-schedule-list-item-time_table-time_schedule-list">
                                    <li className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item">
                                      <p className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-heading">
                                        {getFlightNumberForDisplay(airline, fs.FReservationFlight[0].air_code, fs.FReservationFlight[0].air_sub_no, i18n.language)}
                                      </p>
                                      <ul className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time">
                                        <li className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                          <span className="fz-10 fz-12-md w-5em">
                                            {t(`Airport.${getFlightCodeLabel(fs.FReservationFlight[0].depapo) ?? ""}`)?.replace(/(?:空港|Airport)/gi, "")}
                                          </span>
                                          <span className="fz-18 fz-24-md c-navy fw-b mb-2 mb-4-md">
                                            {getFlightTime(fs.FReservationFlight[0].dep)}
                                          </span>
                                        </li>
                                        <li className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                          <span className="fz-10 fz-12-md w-5em">
                                            {t(`Airport.${getFlightCodeLabel(fs.FReservationFlight[0].arrapo) ?? ""}`)?.replace(/(?:空港|Airport)/gi, "")}
                                          </span>
                                          <span className="fz-14 fz-20-md c-navy fw-b mb-4 mb-6-md">
                                            {getFlightTime(fs.FReservationFlight[0].arv)}
                                          </span>
                                        </li>
                                      </ul>
                                    </li>

                                    {getConnectionFlights(fs.FReservationFlight[0].FReservationConnectionFlight, []).map((cf, cIndex) => (
                                      <li key={"add-conn-" + cIndex} className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item">
                                        <p className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-heading">
                                          {getFlightNumberForDisplay(airline, cf.air_code, cf.air_sub_no, i18n.language)}
                                        </p>
                                        <ul className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time">
                                          <li className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                            <span className="fz-10 fz-12-md w-5em">
                                              {t(`Airport.${getFlightCodeLabel(cf.depapo)}`)?.replace(/(?:空港|Airport)/gi, "")}
                                            </span>
                                            <span className="fz-18 fz-24-md c-navy fw-b mb-2 mb-4-md">{getFlightTime(cf.dep)}</span>
                                          </li>
                                          <li className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                            <span className="fz-10 fz-12-md w-5em">
                                              {t(`Airport.${getFlightCodeLabel(cf.arrapo)}`)?.replace(/(?:空港|Airport)/gi, "")}
                                            </span>
                                            <span className="fz-14 fz-20-md c-navy fw-b mb-4 mb-6-md">{getFlightTime(cf.arv)}</span>
                                          </li>
                                        </ul>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              <div className="box_select-flight-schedule-list-item-seat">
                                {fs.FReservationFlight.map((rf, seatIndex) => {
                                  const vacancy = isVacancy(airline, rf, planSelectFormData.searchParams.roomDetail);
                                  const inputId = "add-flight-input-" + fIndex + "-" + seatIndex;
                                  return (
                                    <div
                                      key={inputId}
                                      className={vacancy
                                        ? "box_select-flight-schedule-list-item-seat-content"
                                        : "box_select-flight-schedule-list-item-seat-content invalid"}
                                    >
                                      <ul className="box_select-flight-schedule-list-item-seat-content-list">
                                        <li className="box_select-flight-schedule-list-item-seat-content-list-label">
                                          <input
                                            type="radio"
                                            name="additional-flight"
                                            id={inputId}
                                            value={createReserveFlightNumber(rf)}
                                            checked={isSelected(rf)}
                                            disabled={!vacancy}
                                            onChange={() => handleFlightSelect(rf)}
                                          />
                                          <label htmlFor={inputId} className="fz-12 fz-14-md c-navy fw-b">
                                            {t(getFlightClassLabel(rf.class_code))}
                                          </label>
                                        </li>
                                        <li className="box_select-flight-schedule-list-item-seat-content-list-status">
                                          <label htmlFor={inputId} className="box_select-flight-schedule-list-item-seat-content-list-status-label">
                                            <span className="d-b fz-10 fz-14-md fw-b c-navy">{t("空席")}</span>
                                            <i className={vacancy ? "icon-possible ml-8-md mr-8-md" : "icon-impossible ml-8-md mr-8-md"}></i>
                                          </label>
                                        </li>
                                        <li className="box_select-flight-schedule-list-item-seat-content-list-result">
                                          <label htmlFor={inputId}>
                                            <p className="box_select-flight-schedule-list-item-seat-content-list-result-text">
                                              {getAdultFee(rf).toLocaleString()}<span className="fz-12">{t("円")}</span>
                                            </p>
                                          </label>
                                        </li>
                                      </ul>
                                    </div>
                                  );
                                })}
                              </div>
                            </li>
                          </React.Fragment>
                        ))
                      ) : (
                        <li className="p-16">{mixedMessage || t("該当するフライトが見つかりませんでした。")}</li>
                      )}
                    </ul>
                  </div>

                  {/* ボタンが2つになると固定高（138px / md 155px）に収まらないため
                      height:auto の `variable` を付ける（issue #3285） */}
                  <div className={showTripTypeChoice ? "flight-modal-fixed-bottom variable" : "flight-modal-fixed-bottom"}>
                    {/* 確定エリア（issue #3285）。
                        1便目（または片道区間の編集中）は「復路を選択する」と
                        「この便のみで確定（片道）」を並べ、ここで往復/片道を決めさせる。
                        それ以外（復路ステップ・往復区間の片方向編集）は従来どおり単一ボタン。

                        レイアウト: スマホは縦積み（横並びだと日本語ラベルが長く溢れる）、
                        md 以上で横並び。`gap` は flex 時のみ効くので、縦積み側は mb-8 で間隔を取る。
                        ボタンは両方とも .flight-modal-fixed-bottom 内で font-size:18px が当たり
                        `padding-large` も効くクラス（orange / light_blue）に揃えている
                        （button-border-medium は両方とも当たらずサイズが不揃いになる）。 */}
                    {showTripTypeChoice ? (
                      <div className="d-b d-f-md" style={{ gap: 12 }}>
                        <button
                          type="button"
                          onClick={handleProceedToReturn}
                          disabled={!selectedFlights || hardBlock}
                          className="button-medium-orange padding-large w-100per mb-8 mb-0-md"
                        >
                          {t("復路を選択する")}
                        </button>
                        <button
                          type="button"
                          onClick={handleDecide}
                          disabled={!selectedFlights || hardBlock}
                          className="button-medium-light_blue padding-large w-100per"
                        >
                          {t("この便のみで確定（片道）")}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={singleButtonProceeds ? handleProceedToReturn : handleDecide}
                        disabled={!selectedFlights || hardBlock}
                        className="button-medium-orange padding-large w-100per"
                      >
                        {buttonLabel}
                      </button>
                    )}

                    <div className="wrap-content-900-no_padding">
                      <div className="box-white fixed-padding">
                        <div className="d-f ai-c jc-sb jc-fe-md">
                          <p className="fw-b pr-24">
                            {t("追加するフライト料金")}<br />
                            <span className="fz-12 fw-n">（※{t("1人あたり")}）</span>
                          </p>
                          <p className="fz-24 fz-28-md c-red fw-b">
                            {selectedFlights ? (
                              <>
                                {getAdultFee(selectedFlights).toLocaleString()}
                                <span className="fz-14 fz-16-md">{t("円")}({t("税込")})</span>
                              </>
                            ) : (
                              <span className="fw-b fz-16">{t("フライトを選択してください")}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </LoadingOverlay>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdditionalFlightModal;
